import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Ban, Search, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function OptedOut() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null); // contact_id currently being opted in
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/contacts/opted-out");
      setContacts(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = contacts.filter(c => {
    if (!q) return true;
    const hay = `${c.display_name || ""} ${c.first_name || ""} ${c.last_name || ""} ${c.phone || ""} ${c.email || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const optIn = async (c) => {
    if (!window.confirm(`Opt ${c.display_name || c.phone} back in? Only do this if they explicitly asked to receive texts again.`)) return;
    setBusy(c.id);
    try {
      await api.post(`/contacts/${c.id}/opt-in`);
      toast.success(`${c.display_name || c.phone} opted back in`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="opted-out-page">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="flex items-start md:items-end justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Ban className="w-6 h-6 md:w-7 md:h-7 text-[#D9232E]" /> Opted Out
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Contacts who replied <span className="font-semibold text-slate-700">STOP</span> or were manually opted out. They cannot receive any texts from STS Connect until they reply <span className="font-semibold text-slate-700">START</span> or you opt them back in.
            </p>
          </div>
          <div className="text-xs text-slate-500 shrink-0">{contacts.length} opted out</div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search opted-out contacts..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="opted-out-search-input"
            />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Desktop table */}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 font-semibold">Opted out</th>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody data-testid="opted-out-table">
              {loading && (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400">Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400">🎉 Nobody has opted out. You're in the clear.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`opted-out-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.phone}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{fmtDate(c.opted_out_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    <Badge variant="secondary" className="text-[10px]">
                      {c.opted_out_source === "manual"
                        ? `manual${c.opted_out_by ? ` · ${c.opted_out_by.split("@")[0]}` : ""}`
                        : "replied STOP"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => optIn(c)}
                      disabled={busy === c.id}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`opt-in-button-${c.id}`}
                    >
                      {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Opt back in</>)}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {loading && <div className="text-center py-10 text-slate-400 text-sm">Loading...</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-10 text-slate-400 text-sm">🎉 Nobody has opted out.</div>
            )}
            {filtered.map((c) => (
              <div key={c.id} className="p-4" data-testid={`opted-out-card-${c.id}`}>
                <div className="flex justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">
                      {c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.phone}</div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      Opted out {fmtDate(c.opted_out_at)} · {c.opted_out_source === "manual" ? "manual" : "replied STOP"}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => optIn(c)}
                    disabled={busy === c.id}
                    className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`opt-in-button-mobile-${c.id}`}
                  >
                    {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Opt in"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px] text-slate-400 mt-4 leading-relaxed">
          Legal note: SMS carriers require you to honor STOP requests immediately. Only opt someone back in when they've asked you to (in person, by phone, or by texting START).
        </div>
      </div>
    </div>
  );
}
